import { Alert, Badge, Button, buttonClass, cardClass, fieldClass, selectClass, Table } from '@ledgerloom/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, propose } from '../api';
import { useApi, useDebounced, usePageTitle, useWorkspaceCurrency } from '../hooks';
import { useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Loading, outcomeMessage, outcomeTone, Reasons, Time, useToast, badgeTone } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { day, money, toMinor } from '../format';

// Quotes and the product catalog. Every amount comes from the quote_price
// engine; the screen only shows it. Patterns: data-display/table, forms for
// the line editor, content-management/modal for each decision, navigation/load-more
// for the quote list, and forms/search-field for the new quote's deal picker.

interface DealOption { id: string; title: string; currency: string }
const DEAL_PICKS = 50;

interface QuoteRow { id: string; number: string; version: number; status: string; approval_state: string; total_minor: string; currency: string; valid_until: string; deal_id: string | null; deal_title: string | null; party_name: string | null; created_at: string }
interface QuotePage { items: QuoteRow[]; next: string | null }
interface Product { id: string; sku: string; name: string; unit: string; unit_price_minor: string; currency: string; tax_bp: number; recurring: string; status: string; version: number }

export function Quotes() {
  const quotes = useApi<QuotePage>('/v1/quotes?limit=100');
  // Pages after the first, appended by Load more.
  const [extra, setExtra] = useState<QuotePage | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const rows = [...(quotes.data?.items ?? []), ...(extra?.items ?? [])];
  const next = extra ? extra.next : quotes.data?.next ?? null;
  usePageTitle('Quotes');
  async function more() {
    if (!next) return;
    setLoadingMore(true); setMoreError(null);
    try {
      const r = await api<QuotePage>(`/v1/quotes?limit=100&after=${next}`);
      setExtra((cur) => ({ items: [...(cur?.items ?? []), ...r.items], next: r.next }));
    } catch (e) { setMoreError(e); } finally { setLoadingMore(false); }
  }
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Quotes</h1><p>Priced by the quote engine; large ones wait for a manager. A sent quote never changes: a correction is a new version.</p></div>
        <div className="row"><Link className={buttonClass({ variant: 'secondary' })} to="/products"><Icon name="box" />Products</Link><Link className={buttonClass()} to="/quotes/new"><Icon name="plus" />New quote</Link></div>
      </div>
      <ErrorBanner error={quotes.error} />
      <section className={cardClass}>
        {quotes.loading && !quotes.data ? <ListSkeleton rows={4} label="Loading quotes" /> : !rows.length ? <Empty title="No quotes yet" action={<Link className={buttonClass({ size: 'sm' })} to="/quotes/new">New quote</Link>} /> : (
          <Table label="Quotes" id="quote-list">
            <caption className="sr-only">Quotes, newest first</caption>
            <thead><tr><th scope="col">Quote</th><th scope="col">Deal</th><th scope="col">Status</th><th scope="col">Total</th><th scope="col" className="hide-sm">Valid until</th><th scope="col" className="hide-sm">Created</th></tr></thead>
            <tbody>{rows.map((q) => <tr key={q.id}><td><Link to={`/quotes/${q.id}`}>{q.number} v{q.version}</Link></td><td className="small">{q.deal_title ?? q.party_name ?? '–'}</td><td><QuoteStatus s={q.status} approval={q.approval_state} /></td><td>{money(q.total_minor, q.currency)}</td><td className="small hide-sm">{day(q.valid_until)}</td><td className="small hide-sm"><Time iso={q.created_at} /></td></tr>)}</tbody>
          </Table>
        )}
      </section>
      <ErrorBanner error={moreError} />
      {next && <div><Button type="submit" variant="secondary" onClick={more} disabled={loadingMore} aria-controls="quote-list">{loadingMore ? 'Loading…' : 'Load more quotes'}</Button></div>}
    </div>
  );
}

function QuoteStatus({ s, approval }: { s: string; approval: string }) {
  const tone = s === 'accepted' ? 'ok' : s === 'declined' || s === 'expired' ? 'danger' : s === 'submitted' ? 'warn' : s === 'approved' || s === 'sent' ? 'info' : '';
  return <Badge tone={badgeTone(tone)}>{s}{s === 'submitted' && approval === 'pending' ? ', waiting for a manager' : ''}</Badge>;
}

export function QuoteNew() {
  const [params] = useSearchParams();
  const revises = params.get('revises');
  const [dealQ, setDealQ] = useState('');
  const dq = useDebounced(dealQ);
  const deals = useApi<DealOption[]>(`/v1/deals?status=open&limit=${DEAL_PICKS}${dq ? `&q=${encodeURIComponent(dq)}` : ''}`);
  const products = useApi<Product[]>('/v1/products?status=active');
  const [dealId, setDealId] = useState(params.get('deal') ?? '');
  // The chosen deal stays an option while a search shows others; one named in the link is fetched.
  const [picked, setPicked] = useState<DealOption | null>(null);
  const linked = useApi<DealOption>(!revises && params.get('deal') ? `/v1/deals/${params.get('deal')}` : null);
  const chosen = [picked, linked.data].find((d) => d?.id === dealId) ?? null;
  const dealOptions = chosen && !deals.data?.some((d) => d.id === chosen.id) ? [chosen, ...(deals.data ?? [])] : deals.data ?? [];
  const workspaceCurrency = useWorkspaceCurrency();
  const [lines, setLines] = useState<Array<{ product_id: string; quantity: string; discount: string }>>([{ product_id: '', quantity: '1', discount: '' }]);
  const [discount, setDiscount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [rejected, setRejected] = useState<any>(null);
  const toast = useToast();
  const navigate = useNavigate();
  usePageTitle(revises ? 'Revise quote' : 'New quote');
  const byId = useMemo(() => new Map((products.data ?? []).map((p) => [p.id, p])), [products.data]);
  const estimate = lines.reduce((n, l) => { const p = byId.get(l.product_id); return n + (p ? Number(p.unit_price_minor) * Number(l.quantity || 0) * (1 - Number(l.discount || 0) / 100) : 0); }, 0) * (1 - Number(discount || 0) / 100);

  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null); setRejected(null);
    try {
      const payload = {
        ...(revises ? { revises_quote_id: revises } : { deal_id: dealId }),
        lines: lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), ...(l.discount ? { discount_bp: Math.round(Number(l.discount) * 100) } : {}) })),
        ...(discount ? { discount_bp: Math.round(Number(discount) * 100) } : {}),
      };
      const out = await propose('quote.create', payload);
      if (out.state === 'rejected') { setRejected(out); return; }
      toast(outcomeMessage(out, 'Quote drafted'), outcomeTone(out));
      if (out.result?.quote_id) navigate(`/quotes/${out.result.quote_id}`); else navigate('/quotes');
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/quotes', label: 'Quotes' }, { label: revises ? 'Revise' : 'New quote' }]} />
      <div className="page-head"><div><h1>{revises ? 'Revise quote' : 'New quote'}</h1><p>Pick products and quantities. The engine prices the quote when you save; the estimate here is only a guide.</p></div></div>
      <form className={`${cardClass} card-pad stack`} onSubmit={save}>
        <ErrorBanner error={error} focusOnShow />
        {rejected && <Alert tone="danger" role="alert"><Reasons reasons={rejected.reasons} /></Alert>}
        {!revises && (
          <Field label="Search open deals" hint={deals.data && deals.data.length === DEAL_PICKS && !dq ? `The ${DEAL_PICKS} newest are listed; type part of a title to find another.` : 'Narrows the deal list below by title.'}>
            <input className={fieldClass} type="search" style={{ maxWidth: 360 }} value={dealQ} onChange={(e) => setDealQ(e.target.value)} />
          </Field>
        )}
        {!revises && (
          <Field label="Deal" required>
            <select className={selectClass} value={dealId} onChange={(e) => { setDealId(e.target.value); setPicked(dealOptions.find((d) => d.id === e.target.value) ?? null); }}><option value="">Choose a deal…</option>{dealOptions.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}</select>
          </Field>
        )}
        <fieldset className="field"><legend>Lines</legend>
          <div className="stack" style={{ gap: 8 }}>
            {lines.map((l, i) => (
              <div key={i} className="row" style={{ alignItems: 'flex-end' }}>
                <Field label={`Product, line ${i + 1}`} className="grow"><select className={selectClass} value={l.product_id} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, product_id: e.target.value } : x)))}><option value="">Choose a product…</option>{products.data?.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.unit_price_minor, p.currency)} per {p.unit}</option>)}</select></Field>
                <Field label="Quantity"><input className={fieldClass} style={{ width: 100 }} inputMode="decimal" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} /></Field>
                <Field label="Discount %"><input className={fieldClass} style={{ width: 100 }} inputMode="decimal" value={l.discount} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, discount: e.target.value } : x)))} /></Field>
                {lines.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => setLines(lines.filter((_, j) => j !== i))}>Remove<span className="sr-only"> line {i + 1}</span></Button>}
              </div>
            ))}
            <div><Button type="button" variant="secondary" size="sm" onClick={() => setLines([...lines, { product_id: '', quantity: '1', discount: '' }])}><Icon name="plus" />Add a line</Button></div>
          </div>
        </fieldset>
        <Field label="Quote discount %" hint="Spread across the lines before tax."><input className={fieldClass} style={{ maxWidth: 140 }} inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} /></Field>
        <div className="row"><span className="grow muted" role="status">Estimate before tax: {money(Math.round(estimate), chosen?.currency ?? workspaceCurrency ?? 'USD')}</span><Button type="submit" disabled={busy}>{busy ? 'Pricing…' : 'Save draft quote'}</Button></div>
      </form>
    </div>
  );
}

interface Quote extends QuoteRow {
  subtotal_minor: string; discount_minor: string; tax_minor: string; terms: string | null; accepted_by_name: string | null; declined_reason: string | null; pricing: any;
  lines: Array<{ id: string; position: number; description: string; sku: string | null; quantity: string; unit_price_minor: string; discount_bp: number; tax_bp: number; net_minor: string; tax_minor: string }>;
  versions: Array<{ id: string; version: number; status: string; total_minor: string }>;
}

export function QuoteDetail() {
  const { id = '' } = useParams();
  const quote = useApi<Quote>(`/v1/quotes/${id}`, [id]);
  const [dialog, setDialog] = useState<'submit' | 'sent' | 'accept' | 'decline' | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [blank, setBlank] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { refreshStats } = useSession();
  const q = quote.data;
  usePageTitle(q ? `Quote ${q.number}` : 'Quote');

  async function act() {
    if ((dialog === 'accept' || dialog === 'decline') && !text.trim()) { setBlank(true); return; }
    setBusy(true); setError(null);
    try {
      const out = dialog === 'submit' ? await propose('quote.submit', { quote_id: id, ...(text ? { note: text } : {}) })
        : dialog === 'sent' ? await propose('quote.mark_sent', { quote_id: id })
        : dialog === 'accept' ? await propose('quote.accept', { quote_id: id, accepted_by_name: text.trim(), source: 'person' })
        : await propose('quote.decline', { quote_id: id, reason: text.trim(), source: 'person' });
      toast(outcomeMessage(out, 'Saved'), outcomeTone(out));
      setDialog(null); setText(''); refreshStats(); void quote.reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  if (quote.loading && !q) return <Loading label="Loading the quote" />;
  if (!q) return <ErrorBanner error={quote.error} />;
  const current = q.versions[q.versions.length - 1]?.id === q.id;
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/quotes', label: 'Quotes' }, { label: `${q.number} v${q.version}` }]} />
      <div className="page-head">
        <div><div className="row"><h1>Quote {q.number}, version {q.version}</h1><QuoteStatus s={q.status} approval={q.approval_state} /></div><p>{q.deal_title ? <Link to={`/deals/${q.deal_id}`}>{q.deal_title}</Link> : q.party_name} · valid until {day(q.valid_until)}</p></div>
        <div className="row">
          {q.status === 'draft' && <Button type="submit" onClick={() => setDialog('submit')}>Submit for approval</Button>}
          {q.status === 'approved' && <Button type="submit" onClick={() => setDialog('sent')}>Record as sent</Button>}
          {['approved', 'sent'].includes(q.status) && <><Button type="submit" variant="secondary" onClick={() => setDialog('accept')}>Record acceptance…</Button><Button type="submit" variant="danger" onClick={() => setDialog('decline')}>Record decline…</Button></>}
          {current && ['draft', 'submitted', 'approved', 'sent'].includes(q.status) && <Button type="submit" variant="ghost" onClick={() => navigate(`/quotes/new?revises=${q.id}`)}>Revise</Button>}
        </div>
      </div>
      <ErrorBanner error={dialog ? null : error} />
      <section className={cardClass}>
        <Table label="Quote lines">
          <caption className="sr-only">Lines</caption>
          <thead><tr><th scope="col">Item</th><th scope="col">Qty</th><th scope="col">Unit price</th><th scope="col">Discount</th><th scope="col">Net</th><th scope="col">Tax</th></tr></thead>
          <tbody>{q.lines.map((l) => <tr key={l.id}><td>{l.description}{l.sku ? <div className="faint">{l.sku}</div> : null}</td><td>{Number(l.quantity)}</td><td>{money(l.unit_price_minor, q.currency)}</td><td>{l.discount_bp ? `${l.discount_bp / 100}%` : '–'}</td><td>{money(l.net_minor, q.currency)}</td><td>{money(l.tax_minor, q.currency)}</td></tr>)}</tbody>
        </Table>
        <div className="card-pad row">
          <code className="engine">quote_price {q.pricing?.engine_version ?? q.pricing?._engine_version ?? '1.0.0'}</code>
          <dl className="facts totals">
            <dt>Subtotal</dt><dd>{money(q.subtotal_minor, q.currency)}</dd>
            <dt>Discounts</dt><dd>−{money(q.discount_minor, q.currency)}</dd>
            <dt>Tax</dt><dd>{money(q.tax_minor, q.currency)}</dd>
            <dt><strong>Total</strong></dt><dd><strong>{money(q.total_minor, q.currency)}</strong></dd>
          </dl>
        </div>
      </section>
      {q.accepted_by_name && <Alert role="note" tone="success">Accepted by {q.accepted_by_name}.</Alert>}
      {q.declined_reason && <Alert role="note" tone="danger">Declined: {q.declined_reason}</Alert>}
      {q.versions.length > 1 && <section className={`${cardClass} card-pad`}><h2>Versions</h2><ul className="small">{q.versions.map((v) => <li key={v.id}>{v.id === q.id ? `v${v.version} (this one)` : <Link to={`/quotes/${v.id}`}>v{v.version}</Link>} · {v.status} · {money(v.total_minor, q.currency)}</li>)}</ul></section>}
      <ConfirmDialog open={!!dialog} title={dialog === 'submit' ? 'Submit this quote?' : dialog === 'sent' ? 'Record this quote as sent?' : dialog === 'accept' ? 'Record that the customer accepted?' : 'Record that the customer declined?'}
        confirmLabel={dialog === 'submit' ? 'Submit' : dialog === 'sent' ? 'Record as sent' : dialog === 'accept' ? 'Record acceptance' : 'Record decline'} tone={dialog === 'decline' ? 'danger' : 'primary'} busy={busy} onConfirm={act} onCancel={() => { setDialog(null); setText(''); setBlank(false); }}>
        <p style={{ margin: 0 }}>{dialog === 'submit' ? 'A quote over the approval threshold, or with a deep discount, waits for a manager.' : dialog === 'sent' ? 'Once sent, the quote is frozen. A change becomes a new version.' : 'This is recorded with your name in the decision log.'}</p>
        <ErrorBanner error={error} />
        {dialog === 'accept' && <Field label="Accepted by" required error={blank ? 'Name the person who accepted.' : null}><input className={fieldClass} value={text} onChange={(e) => { setText(e.target.value); setBlank(false); }} /></Field>}
        {dialog === 'decline' && <Field label="Why did they decline?" required error={blank ? 'Say why they declined.' : null}><input className={fieldClass} value={text} onChange={(e) => { setText(e.target.value); setBlank(false); }} /></Field>}
        {dialog === 'submit' && <Field label="Note for the approver"><input className={fieldClass} value={text} onChange={(e) => setText(e.target.value)} /></Field>}
      </ConfirmDialog>
    </div>
  );
}

export function Products() {
  const products = useApi<Product[]>('/v1/products');
  const [f, setF] = useState({ sku: '', name: '', unit: 'each', price: '', tax: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const currency = useWorkspaceCurrency();
  usePageTitle('Products');
  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!currency) { setError(new Error('The workspace currency is still loading. Try again.')); return; }
    setBusy(true); setError(null);
    try {
      const out = await propose('product.create', { sku: f.sku, name: f.name, unit: f.unit, price: { minor: toMinor(f.price || '0'), scale: 4, currency }, ...(f.tax ? { tax_bp: Math.round(Number(f.tax) * 100) } : {}) });
      if (out.state === 'rejected') { setError(new Error(outcomeMessage(out))); return; }
      toast(outcomeMessage(out, 'Product added'), outcomeTone(out)); setF({ sku: '', name: '', unit: 'each', price: '', tax: '' }); void products.reload();
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  const [archiving, setArchiving] = useState<Product | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  async function archive() {
    const p = archiving; if (!p) return;
    setArchiveBusy(true);
    try { const out = await propose('product.update', { product_id: p.id, base_version: p.version, set: { status: 'archived' } }); toast(outcomeMessage(out, `${p.name} archived`), outcomeTone(out)); setArchiving(null); void products.reload(); }
    catch (err) { toast((err as Error).message, 'error'); } finally { setArchiveBusy(false); }
  }
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/quotes', label: 'Quotes' }, { label: 'Products' }]} />
      <div className="page-head"><div><h1>Products</h1><p>The catalog quotes price from. Archived products stay on old quotes.</p></div></div>
      <ErrorBanner error={products.error} />
      <section className={cardClass}>
        {products.loading && !products.data ? <ListSkeleton rows={3} label="Loading products" /> : !products.data?.length ? <Empty title="No products yet" /> : (
          <Table label="Products">
            <caption className="sr-only">Product catalog</caption>
            <thead><tr><th scope="col">Product</th><th scope="col">SKU</th><th scope="col">Price</th><th scope="col">Tax</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{products.data.map((p) => <tr key={p.id}><td>{p.name}</td><td className="mono">{p.sku}</td><td>{money(p.unit_price_minor, p.currency)} per {p.unit}{p.recurring !== 'none' ? `, ${p.recurring}ly` : ''}</td><td>{p.tax_bp ? `${p.tax_bp / 100}%` : '–'}</td><td><Badge>{p.status}</Badge></td><td>{p.status === 'active' && <Button type="submit" variant="ghost" size="sm" onClick={() => setArchiving(p)}>Archive<span className="sr-only"> {p.name}</span></Button>}</td></tr>)}</tbody>
          </Table>
        )}
        <form className="card-pad form-grid" style={{ borderTop: '1px solid var(--border)' }} onSubmit={add}>
          <h2 className="full">Add a product</h2>
          <div className="full"><ErrorBanner error={error} focusOnShow /></div>
          <Field label="Name" required><input className={fieldClass} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="SKU" required><input className={fieldClass} value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></Field>
          <Field label={`Price (${currency ?? '…'})`} required><input className={fieldClass} inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></Field>
          <Field label="Unit"><select className={selectClass} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}>{['each', 'seat', 'hour', 'day', 'month', 'year', 'project'].map((u) => <option key={u}>{u}</option>)}</select></Field>
          <Field label="Tax %"><input className={fieldClass} inputMode="decimal" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} /></Field>
          <div className="full"><Button type="submit" variant="secondary" disabled={busy}>{busy ? 'Adding…' : 'Add product'}</Button></div>
        </form>
      </section>
      <ConfirmDialog open={!!archiving} title={`Archive ${archiving?.name ?? ''}?`} confirmLabel="Archive product" tone="danger" busy={archiveBusy} onConfirm={() => void archive()} onCancel={() => setArchiving(null)}>
        <p style={{ margin: 0 }}>New quotes can no longer use it. Quotes that already have it keep it.</p>
      </ConfirmDialog>
    </div>
  );
}
